import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, AfterViewInit, OnDestroy, HostListener } from '@angular/core';

export interface SliderOptions {
  initialSlide?: number;
  speed?: number;
  pagination?: object;
  slidesPerView?: number;
  spaceBetween?: number;
  autoplay?: boolean | { delay: number };
  loop?: boolean;
}

export interface PeopleSay {
  img: string;
  title: string;
  via: string;
  body: string;
  socialIcon: string;
}

const DEFAULT_PEOPLE_SAY_LIST: PeopleSay[] = [];

@Component({
    selector: 'upsell-slider',
    templateUrl: './upsell-slider.html',
    styleUrls: ['./upsell-slider.scss'],
    standalone: false
})
export class UpsellSliderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sliderContainer', { static: false }) sliderContainer!: ElementRef;
  @ViewChild('slidesWrapper', { static: false }) slidesWrapper!: ElementRef;

  @Input() options: SliderOptions = {
    slidesPerView: 1,
    spaceBetween: 0,
    speed: 300,
    autoplay: false,
    loop: false
  };

  @Input() peopleSayList: PeopleSay[] = [];
  // Rendered slides, derived from peopleSayList in ngOnInit. Public (not @Input) so tests can set it.
  slides: any[] = [];
  @Input() leftArrowContent = '<img src="https://cdn.badlandsranch.com/images/arrow-left2511.svg" alt="left arrow">';
  @Input() rightArrowContent = '<img src="https://cdn.badlandsranch.com/images/arrow-right2511.svg" alt="right arrow">';
  @Input() pager = true;
  @Input() showPagination = true;

  @Output() goPreviousEvent = new EventEmitter<number>();
  @Output() goNextEvent = new EventEmitter<number>();
  @Output() slideChangeEvent = new EventEmitter<number>();

  currentSlide = 0;
  autoplayInterval: any;
  isTransitioning = false;

  ngOnInit() {
    // Derive slides from the testimonials list: a JSON override (peopleSayList) or the built-in default.
    if (!this.slides.length) {
      const source = this.peopleSayList?.length ? this.peopleSayList : DEFAULT_PEOPLE_SAY_LIST;
      this.slides = this.buildSlides(source);
    }
  }

  ngAfterViewInit() {
    this.initializeSlider();
    if (this.options.autoplay) {
      this.startAutoplay();
    }
    
    // Initial height update after view is ready - use multiple timeouts to ensure content is rendered
    setTimeout(() => {
      this.updateContainerHeight();
    }, 0);
    
    // Additional height update after a longer delay to catch any late-rendering content
    setTimeout(() => {
      this.updateContainerHeight();
    }, 100);
    
    // Final height update to ensure everything is properly sized
    setTimeout(() => {
      this.updateContainerHeight();
    }, 300);
  }

  ngOnDestroy() {
    this.stopAutoplay();
  }

  private buildSlides(list: PeopleSay[]): any[] {
    return list.map((el) => ({
      image: el.img,
      title: el.title,
      content: el.body,
      alt: `${el.title} ${el.via}`,
      via: el.via,
      socialIcon: el.socialIcon
    }));
  }

  private initializeSlider() {
    if (this.slides.length === 0) return;
    
    this.currentSlide = this.options.initialSlide || 0;
    this.updateSliderPosition();
  }

  private updateSliderPosition() {
    if (!this.slidesWrapper) return;
    
    const slideWidth = 100 / (this.options.slidesPerView || 1);
    const translateX = -(this.currentSlide * slideWidth);
    
    this.slidesWrapper.nativeElement.style.transform = `translateX(${translateX}%)`;
    
    // Update container height based on current slide content
    this.updateContainerHeight();
  }

  private updateContainerHeight() {
    if (!this.sliderContainer || !this.slidesWrapper) return;
    
    // Get the current slide element
    const slides = this.sliderContainer.nativeElement.querySelectorAll('.flex-shrink-0');
    const currentSlideElement = slides[this.currentSlide];
    
    if (currentSlideElement) {
      // Find the feed_content element within the current slide
      const feedContent = currentSlideElement.querySelector('.feed_content');
      
      if (feedContent) {
        // Force a reflow to ensure accurate measurements
        feedContent.offsetHeight;
        
        // Reset height to auto to get natural height
        this.sliderContainer.nativeElement.style.height = 'auto';
        this.slidesWrapper.nativeElement.style.height = 'auto';
        
        // Get the natural height of the feed_content
        const contentHeight = feedContent.offsetHeight;
        
        // Only update if we have a valid height (greater than 0)
        if (contentHeight > 0) {
          // Set the container height to match the feed_content
          this.sliderContainer.nativeElement.style.height = contentHeight + 'px';
          
          // Also set the slides wrapper height to ensure proper overflow handling
          this.slidesWrapper.nativeElement.style.height = contentHeight + 'px';
        }
      }
    }
  }

  goPrevious() {
    if (this.isTransitioning) return;
    
    if (this.options.loop) {
      this.currentSlide = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    } else {
      this.currentSlide = Math.max(0, this.currentSlide - 1);
    }
    
    this.updateSliderPosition();
    this.restartAutoplay();
    this.goPreviousEvent.emit(this.currentSlide);
    this.slideChangeEvent.emit(this.currentSlide);
  }

  goNext() {
    if (this.isTransitioning) return;
    
    if (this.options.loop) {
      this.currentSlide = this.currentSlide === this.slides.length - 1 ? 0 : this.currentSlide + 1;
    } else {
      this.currentSlide = Math.min(this.slides.length - 1, this.currentSlide + 1);
    }
    
    this.updateSliderPosition();
    this.restartAutoplay();
    this.goNextEvent.emit(this.currentSlide);
    this.slideChangeEvent.emit(this.currentSlide);
  }

  goToSlide(index: number) {
    if (this.isTransitioning || index === this.currentSlide) return;
    
    this.currentSlide = index;
    this.updateSliderPosition();
    this.restartAutoplay();
    this.slideChangeEvent.emit(this.currentSlide);
  }

  private startAutoplay() {
    if (!this.options.autoplay) return;
    
    const delay = typeof this.options.autoplay === 'object' ? this.options.autoplay.delay : 3000;
    
    this.autoplayInterval = setInterval(() => {
      this.goNext();
    }, delay);
  }

  private stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  private restartAutoplay() {
    this.stopAutoplay();
    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  @HostListener('window:resize')
  onResize() {
    // Recalculate height when window is resized
    setTimeout(() => {
      this.updateContainerHeight();
    }, 100);
  }

  // Method to handle image load events that might affect height
  onImageLoad() {
    // Recalculate height when images finish loading
    setTimeout(() => {
      this.updateContainerHeight();
    }, 50);
  }
}
